import { useMemo, useState } from "react";
import type { Species } from "../lib/types";
import { rarityTier, RARITY_ORDER, type RarityTier } from "../lib/rarity";
import { SpeciesCard, type SpeciesCardState } from "./SpeciesCard";
import { CloseIcon, SceneFallback, SectionLabel } from "../ui";

// Lagoon SpeciesList — filter row (one chip spec), section label, then
// the abyss SpeciesCard stack. Empty state uses a SceneFallback tile so
// the missing case looks intentional (§7.2).

export function SpeciesList({
  species,
  stateOf,
  onToggle,
  onRemove,
  onCompare,
  onInfo,
}: {
  species: Species[];
  stateOf: (s: Species) => SpeciesCardState;
  onToggle: (s: Species) => void;
  onRemove?: (s: Species) => void;
  onCompare: (s: Species) => void;
  onInfo?: (s: Species) => void;
}) {
  const [filter, setFilter] = useState("");
  const [tier, setTier] = useState<RarityTier | "All">("All");

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return species.filter((s) => {
      if (tier !== "All" && rarityTier(s.observation_count) !== tier) {
        return false;
      }
      if (!q) return true;
      return (
        s.common_name.toLowerCase().includes(q) ||
        s.scientific_name.toLowerCase().includes(q)
      );
    });
  }, [species, filter, tier]);

  if (species.length === 0) {
    return (
      <div className="mx-[18px] overflow-hidden rounded-card bg-abyss shadow-abyss">
        <SceneFallback
          variant="deep"
          silhouette="fish2"
          className="h-32 w-full"
          silhouetteClass="w-16 left-[46%] top-8 opacity-60"
        />
        <div className="px-4 pb-4 pt-3 text-center">
          <p className="font-display text-lg font-semibold text-paper-ink">
            No marine life recorded here
          </p>
          <p className="mt-1 text-sm text-paper-ink/60">
            Try a wider radius, or a nearby named reef or bay.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="mx-[18px] flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative sm:max-w-xs sm:flex-1">
          <input
            className="field w-full pr-9"
            placeholder="Filter by name…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          {filter && (
            <button
              type="button"
              onClick={() => setFilter("")}
              aria-label="Clear filter"
              className="absolute inset-y-0 right-1 my-auto grid h-11 w-11 place-items-center rounded-full text-meta transition hover:bg-ink/10 hover:text-ink"
            >
              <CloseIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div
          className="inline-flex flex-wrap gap-1.5"
          role="group"
          aria-label="Filter by rarity"
        >
          {(["All", ...RARITY_ORDER] as const).map((t) => {
            const on = tier === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTier(t)}
                // Announce which tier is active — without aria-pressed a
                // screen reader reads all four identically. See
                // species-list-rarity-tier-filter-missing-toggle-role.
                aria-pressed={on}
                className={`inline-flex min-h-[44px] items-center rounded-full px-4 py-2 text-sm font-semibold transition
                  ${
                    on
                      ? "bg-aqua-deep text-paper"
                      : "bg-ink/[0.06] text-meta hover:bg-ink/[0.12] hover:text-ink"
                  }`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>

      <SectionLabel className="mx-[18px] mt-4">
        Nearby now · {shown.length}
        {shown.length !== species.length ? ` / ${species.length}` : ""} species
      </SectionLabel>

      {shown.length === 0 ? (
        // Species exist but the name filter / rarity chip narrowed to zero.
        // Without this the grid renders blank whitespace with no guidance.
        // See species-list-filtered-empty-no-message.
        <div className="mx-[18px] rounded-card bg-ink/[0.04] px-4 py-8 text-center">
          <p className="text-sm font-semibold text-ink">
            No species match your filter
          </p>
          <p className="mt-1 text-sm text-meta">
            {tier !== "All"
              ? "Try a different rarity, or clear it to see everything nearby."
              : "Try a different search, or clear it to see everything nearby."}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 px-[18px] lg:grid lg:grid-cols-2 lg:gap-3">
          {shown.map((s, i) => (
            <SpeciesCard
              key={s.taxon_id}
              species={s}
              state={stateOf(s)}
              onToggle={() => onToggle(s)}
              onRemove={onRemove ? () => onRemove(s) : undefined}
              onCompare={() => onCompare(s)}
              onInfo={onInfo ? () => onInfo(s) : undefined}
              // First 4 rows are above the fold on any realistic viewport
              // (78 px thumb + row gap ≈ 100 px each). Let those photos jump
              // the browser's default queue via fetchpriority=high; the rest
              // stay on lazy/auto.
              priority={i < 4}
            />
          ))}
        </div>
      )}
    </div>
  );
}
